import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem,
  FormControlLabel, Switch, Alert, Box, Typography, Avatar,
} from '@mui/material';
import type { AdminRace } from './types';

const apiUrl = process.env.REACT_APP_API_URL;
const STATUSES = ['Live', 'Upcoming', 'Complete'] as const;

function suggestDirectory(name: string, dateText: string) {
  const parsed = new Date(dateText);
  const useDate = !isNaN(parsed.getTime()) ? parsed : new Date();
  // Build the date from local getters, not .toISOString() (which converts to
  // UTC first) — for timezones ahead of UTC, like NZ, that silently rolls
  // local midnight back to the previous day.
  const y = useDate.getFullYear();
  const m = String(useDate.getMonth() + 1).padStart(2, '0');
  const d = String(useDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d} ${name.trim()}`.trim();
}

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  initialRace: AdminRace | null;
  onClose: () => void;
  onSaved: () => void;
};

const RaceFormDialog = ({ open, mode, initialRace, onClose, onSaved }: Props) => {
  const [directory, setDirectory] = useState('');
  const [directoryTouched, setDirectoryTouched] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [status, setStatus] = useState<typeof STATUSES[number]>('Upcoming');
  const [isExternal, setIsExternal] = useState(true);
  const [url, setUrl] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [resultsTxtFile, setResultsTxtFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset the form each time it's opened, seeded from initialRace when editing.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setLogoFile(null);
    setResultsTxtFile(null);
    setDirectoryTouched(mode === 'edit');
    setDirectory(initialRace?.directory || '');
    setName(initialRace?.name || '');
    setDate(initialRace?.date || '');
    setStatus((initialRace?.status as typeof STATUSES[number]) || 'Upcoming');
    setIsExternal(initialRace ? initialRace.isExternal === 'true' : true);
    setUrl(initialRace?.url || '');
  }, [open, mode, initialRace]);

  // Keep the folder-name suggestion in sync with name/date until the admin
  // edits it directly — only relevant for new races (existing ones keep
  // their directory; renaming isn't supported here).
  useEffect(() => {
    if (mode === 'create' && !directoryTouched) {
      setDirectory(suggestDirectory(name, date));
    }
  }, [name, date, mode, directoryTouched]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!directory.trim()) return setError('A folder name is required.');
    if (!name.trim() || !date.trim()) return setError('Name and date are required.');
    if (isExternal && !url.trim()) return setError('External events need a results URL.');

    setSubmitting(true);
    const formData = new FormData();
    if (mode === 'create') formData.append('directory', directory);
    formData.append('name', name);
    formData.append('date', date);
    formData.append('status', status);
    formData.append('isExternal', String(isExternal));
    formData.append('url', url);
    if (logoFile) formData.append('logo', logoFile);
    if (resultsTxtFile) formData.append('resultsTxt', resultsTxtFile);

    const endpoint = mode === 'create'
      ? `${apiUrl}/api/admin/races`
      : `${apiUrl}/api/admin/races/${encodeURIComponent(initialRace!.directory)}`;

    fetch(endpoint, { method: mode === 'create' ? 'POST' : 'PUT', credentials: 'include', body: formData })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Save failed');
        }
      })
      .then(() => onSaved())
      .catch((err) => setError(err.message))
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === 'create' ? 'Add race' : `Edit "${initialRace?.name}"`}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Event name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="e.g. 05-May-2024"
            helperText="Free text — shown as-is on the site, and used to suggest the folder name below."
            required
            fullWidth
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof STATUSES[number])}
            fullWidth
          >
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>

          <FormControlLabel
            control={<Switch checked={isExternal} onChange={(e) => setIsExternal(e.target.checked)} />}
            label="Results hosted externally (e.g. RunSignUp)"
          />
          {isExternal ? (
            <TextField
              label="Results URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required={isExternal}
              fullWidth
            />
          ) : (
            <Alert severity="info">
              Internally-timed events read from a <code>results.txt</code> export. Upload one below now, or
              drop it into this race's folder over SFTP later — either way works.
            </Alert>
          )}

          <TextField
            label="Folder name"
            value={directory}
            onChange={(e) => { setDirectory(e.target.value); setDirectoryTouched(true); }}
            helperText={mode === 'edit' ? "Folders can't be renamed here — delete and recreate to rename." : 'Auto-filled from the name and date; edit if you like.'}
            disabled={mode === 'edit'}
            required
            fullWidth
          />

          <Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>Logo</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {mode === 'edit' && (
                <Avatar
                  variant="rounded"
                  src={`${apiUrl}/result-uploads/${initialRace?.directory}/logo.png`}
                  sx={{ width: 48, height: 48 }}
                />
              )}
              <Button variant="outlined" component="label" size="small">
                {logoFile ? logoFile.name : 'Choose image'}
                <input hidden type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              </Button>
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>Results file (results.txt)</Typography>
            <Button variant="outlined" component="label" size="small">
              {resultsTxtFile ? resultsTxtFile.name : (mode === 'edit' ? 'Replace file' : 'Choose file (optional)')}
              <input hidden type="file" accept=".txt" onChange={(e) => setResultsTxtFile(e.target.files?.[0] || null)} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default RaceFormDialog;
